import React, { useEffect, useMemo, useRef } from 'react';
import { Chart, registerables, ChartData, ChartOptions } from 'chart.js';
import { Report5SChartMonth, Report5SPercentColor } from '../../types/report5SAudits';

Chart.register(...registerables);

const DEFAULT_COLORS: Report5SPercentColor[] = [
  { from: 0, to: 60, color: '#F8B4B4', label: '<60%' },
  { from: 60, to: 80, color: '#FCE96A', label: '≥60 и <80%' },
  { from: 80, to: 101, color: '#BCF0DA', label: '≥80%' },
];

const CROSS_COLOR = '#8899A8';
const CROSS_LABEL = 'Перекрестный аудит';

const colorForPercent = (value: number, palette: Report5SPercentColor[]) => {
  for (const c of palette) {
    if (value >= c.from && value < c.to) return c.color;
  }
  const last = palette[palette.length - 1];
  if (last && value >= last.to) return last.color;
  return palette[0]?.color || '#E5E7EB';
};

const monthLabelsPlugin = {
  id: 'monthLabels5S',
  afterDraw(chart: Chart) {
    const boundaries: Array<{ name: string; startIdx: number; endIdx: number }> | undefined =
      (chart.options as any).plugins?.monthLabels5S?.boundaries;
    if (!boundaries || boundaries.length === 0) return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.data.length === 0) return;

    const ctx = chart.ctx;
    const xAxis = chart.scales.x;

    ctx.save();
    ctx.font = 'normal 13px Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#374151';

    boundaries.forEach((b) => {
      const startEl = meta.data[b.startIdx] as any;
      const endEl = meta.data[b.endIdx] as any;
      if (!startEl || !endEl) return;
      const centerX = (startEl.x + endEl.x) / 2;
      ctx.fillText(b.name, centerX, xAxis.bottom + 18);
    });

    ctx.restore();
  },
};

const zoneInsideBarPlugin = {
  id: 'zoneInsideBar5S',
  afterDatasetsDraw(chart: Chart) {
    const ctx = chart.ctx;
    const barIdx = chart.data.datasets.findIndex((d) => d.label === 'bars');
    if (barIdx === -1) return;
    const meta = chart.getDatasetMeta(barIdx);
    const labels = chart.data.labels as string[];
    const data = chart.data.datasets[barIdx].data as Array<number | null>;

    ctx.save();
    ctx.font = 'normal 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = '#374151';

    const fitText = (text: string, maxWidth: number): string => {
      if (maxWidth <= 0) return '';
      if (ctx.measureText(text).width <= maxWidth) return text;
      for (let i = text.length - 1; i > 0; i--) {
        const candidate = text.slice(0, i) + '…';
        if (ctx.measureText(candidate).width <= maxWidth) return candidate;
      }
      return '';
    };

    labels.forEach((label, index) => {
      const element = meta.data[index] as any;
      if (!element) return;
      if (!data[index] || data[index] <= 0) return;
      const base = element.base ?? chart.scales.y.bottom;
      const short = fitText(label, (element.width ?? 0) - 8);
      if (!short) return;
      ctx.fillText(short, element.x, base - 6);
    });

    ctx.restore();
  },
};

const barValueLabelsPlugin = {
  id: 'barValueLabels5S',
  afterDatasetsDraw(chart: Chart) {
    const ctx = chart.ctx;
    const barDataset = chart.data.datasets.findIndex((d) => d.label === 'bars');
    if (barDataset === -1) return;
    const meta = chart.getDatasetMeta(barDataset);

    ctx.save();
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = '#374151';

    (chart.data.datasets[barDataset].data as Array<number | null>).forEach((value, index) => {
      const element = meta.data[index] as any;
      if (!element) return;
      if (!value || value <= 0) return;
      const y = (element.y || 0) - 4;
      ctx.fillText(`${value}%`, element.x, y);
    });

    ctx.restore();
  },
};

const crossLinePlugin = {
  id: 'crossLine5S',
  afterDatasetsDraw(chart: Chart) {
    const opts: { boundaries: Array<{ startIdx: number; endIdx: number }>; percents: Array<number | null> } | undefined =
      (chart.options as any).plugins?.crossLine5S;
    if (!opts || !opts.boundaries || !opts.percents) return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.data.length === 0) return;

    const ctx = chart.ctx;
    const yScale = chart.scales.y;

    const points: Array<{ x: number; y: number; value: number } | null> = opts.boundaries.map((b, i) => {
      const percent = opts.percents[i];
      if (percent === null || percent === undefined || percent <= 0) return null;
      const startEl = meta.data[b.startIdx] as any;
      const endEl = meta.data[b.endIdx] as any;
      if (!startEl || !endEl) return null;
      const x = (startEl.x + endEl.x) / 2;
      const y = yScale.getPixelForValue(percent);
      return { x, y, value: percent };
    });

    ctx.save();
    ctx.strokeStyle = CROSS_COLOR;
    ctx.lineWidth = 2;
    ctx.beginPath();
    let started = false;
    points.forEach((p) => {
      if (!p) return;
      if (!started) {
        ctx.moveTo(p.x, p.y);
        started = true;
      } else {
        ctx.lineTo(p.x, p.y);
      }
    });
    ctx.stroke();

    points.forEach((p) => {
      if (!p) return;
      ctx.beginPath();
      ctx.fillStyle = CROSS_COLOR;
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.font = 'normal 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = CROSS_COLOR;
    points.forEach((p) => {
      if (!p) return;
      ctx.fillText(`${p.value}%`, p.x, p.y - 8);
    });

    ctx.restore();
  },
};

type Chart5SAuditsProps = {
  zones: string[];
  months: Report5SChartMonth[];
  percentColors: Report5SPercentColor[];
};

function Chart5SAudits({ zones, months, percentColors }: Chart5SAuditsProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  const palette = percentColors.length > 0 ? percentColors : DEFAULT_COLORS;

  const { labels, bars, crossPercents, monthBoundaries, barColors } = useMemo(() => {
    const labels: string[] = [];
    const bars: Array<number | null> = [];
    const barColors: string[] = [];
    const monthBoundaries: Array<{ name: string; startIdx: number; endIdx: number }> = [];
    const crossPercents: Array<number | null> = [];

    months.forEach((m) => {
      if (zones.length === 0) return;
      const startIdx = labels.length;
      zones.forEach((zone) => {
        const percent = m.zonePercents[zone] ?? 0;
        labels.push(zone);
        bars.push(percent > 0 ? percent : null);
        barColors.push(colorForPercent(percent, palette));
      });
      const endIdx = labels.length - 1;
      monthBoundaries.push({ name: m.monthName, startIdx, endIdx });
      crossPercents.push(m.crossPercent !== null && m.crossPercent > 0 ? m.crossPercent : null);
    });

    return { labels, bars, crossPercents, monthBoundaries, barColors };
  }, [zones, months, palette]);

  const chartData: ChartData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'bars',
        data: bars,
        backgroundColor: barColors,
        borderColor: barColors,
        borderSkipped: false,
        borderRadius: 2,
        categoryPercentage: 0.85,
        barPercentage: 0.95,
        yAxisID: 'y',
      },
    ],
  }), [labels, bars, barColors]);

  const chartOptions: ChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { bottom: 28, top: 24 } },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.label}: ${ctx.parsed.y}%`,
        },
      },
      monthLabels5S: { boundaries: monthBoundaries },
      crossLine5S: { boundaries: monthBoundaries, percents: crossPercents },
    } as any,
    scales: {
      x: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value: any) => `${value}%`,
          stepSize: 20,
          color: '#9CA3AF',
        },
        grid: { color: 'rgba(156, 163, 175, 0.3)' },
        border: { display: false },
      },
    },
    interaction: { mode: 'nearest', intersect: true },
  }), [monthBoundaries, crossPercents]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: chartData,
      options: chartOptions,
      plugins: [monthLabelsPlugin, barValueLabelsPlugin, zoneInsideBarPlugin, crossLinePlugin],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartData, chartOptions]);

  const legendItems = [
    ...palette.map((c) => ({ text: c.label || `${c.from}-${c.to}%`, color: c.color, type: 'bar' as const })),
    { text: CROSS_LABEL, color: CROSS_COLOR, type: 'line' as const },
  ];

  const BAR_WIDTH_PX = 60;
  const MIN_CHART_WIDTH = 600;
  const chartWidth = Math.max(MIN_CHART_WIDTH, bars.length * BAR_WIDTH_PX + 80);

  return (
    <div className='w-full'>
      <div className='w-full overflow-x-auto'>
        <div style={{ width: chartWidth, minWidth: '100%', height: '28rem' }}>
          <canvas ref={canvasRef} />
        </div>
      </div>
      <div className='flex justify-center flex-wrap gap-6 mt-3'>
        {legendItems.map((item, i) => (
          <div key={i} className='flex items-center gap-2'>
            {item.type === 'line' ? (
              <div className='flex items-center'>
                <div className='w-6 h-0.5 rounded-full' style={{ backgroundColor: item.color }} />
                <div className='w-2 h-2 rounded-full -ml-1' style={{ backgroundColor: item.color }} />
              </div>
            ) : (
              <div className='w-3 h-3 rounded-sm' style={{ backgroundColor: item.color }} />
            )}
            <span className='text-sm text-gray-600'>{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default React.memo(Chart5SAudits);
